import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import type { Lang, RoundState } from '@polyglot/engine';
import { MODES, validateLanguageSelection } from '@polyglot/engine';
import { LangName, Spinner } from '../components/ui';
import { GameScreen } from '../game/GameScreen';
import type { MatchView } from '../game/online';
import { fetchMatch, OnlineDriver, watchMatch } from '../game/online';
import { ResultsView } from '../game/Results';
import { api, ApiError, ensureSession, serverNow, useMe } from '../lib/api';
import { formatScore } from '../lib/format';
import { reducedMotion, useSettings } from '../lib/settings';
import { recallInvite } from './Live';
import { ContractPreview, LanguagePicker } from './Setup';
import { OpponentActions } from './Social';

const REACTIONS = ['gg', 'bravo', 'wow', 'oops', 'thanks', 'good_luck'] as const;

function Countdown({ onDone }: { onDone: () => void }) {
  const [n, setN] = useState(3);
  useEffect(() => {
    if (n === 0) {
      onDone();
      return;
    }
    const id = window.setTimeout(() => setN((x) => x - 1), reducedMotion() ? 700 : 1000);
    return () => window.clearTimeout(id);
  }, [n, onDone]);
  return (
    <div className="countdown" role="timer" aria-live="assertive">
      {n > 0 ? n : '…'}
    </div>
  );
}

/**
 * Countdown to a server-clock instant (live duels: same reveal time for both players).
 * The clock offset is an estimate: with real network latency it can run slightly ahead, and the
 * server (which never reveals early) may still answer "countdown". So after zero, onDone is
 * retried every 500 ms until the round is revealed (this component then unmounts), 30 times max.
 */
function ServerCountdown({ startsAt, onDone }: { startsAt: number; onDone: () => void }) {
  const [left, setLeft] = useState(startsAt - serverNow());
  useEffect(() => {
    let retries = 0;
    let retry: number | undefined;
    const id = window.setInterval(() => {
      const l = startsAt - serverNow();
      setLeft(l);
      if (l <= 0) {
        window.clearInterval(id);
        onDone();
        retry = window.setInterval(() => {
          if (++retries > 30) window.clearInterval(retry);
          else onDone();
        }, 500);
      }
    }, 100);
    return () => {
      window.clearInterval(id);
      window.clearInterval(retry);
    };
  }, [startsAt, onDone]);
  return (
    <div className="countdown" role="timer" aria-live="assertive">
      {left > 0 ? Math.ceil(left / 1000) : '…'}
    </div>
  );
}

export function MatchScreen({ params }: { params: { id: string } }) {
  const { t, i18n } = useTranslation();
  const [, navigate] = useLocation();
  const { user } = useMe();
  const [view, setView] = useState<MatchView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [counting, setCounting] = useState(false);
  const driverRef = useRef<OnlineDriver | null>(null);
  const [driver, setDriver] = useState<OnlineDriver | null>(null);
  const [siblings, setSiblings] = useState<string[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [presence, setPresence] = useState<Record<number, boolean>>({});
  const presenceRef = useRef<Record<number, boolean>>({});
  const [connection, setConnection] = useState<'open' | 'reconnecting'>('open');
  const viewRef = useRef<MatchView | null>(null);
  viewRef.current = view;
  // The match on screen. A late answer about the previous one (e.g. the finished match, reloaded
  // by its own "rematch" event while we navigate to the rematch) must not replace it.
  const currentId = useRef(params.id);
  currentId.current = params.id;

  const load = useCallback(async () => {
    try {
      const v = await fetchMatch(params.id);
      if (v.id !== currentId.current) return;
      setView(v);
      if (v.me.status === 'playing' && v.me.state && v.rack && !driverRef.current) {
        driverRef.current = new OnlineDriver(v.id, v.me.state);
        setDriver(driverRef.current);
      }
    } catch {
      setError(t('match.loadError'));
    }
  }, [params.id, t]);

  useEffect(() => {
    driverRef.current?.dispose();
    driverRef.current = null;
    setDriver(null);
    setView(null);
    presenceRef.current = {};
    setPresence({});
    void ensureSession().then(load);
    const notify = (text: string, ms: number) => {
      setToast(text);
      window.setTimeout(() => setToast((x) => (x === text ? null : x)), ms);
    };
    let lost = false;
    const stop = watchMatch(
      params.id,
      (type, data) => {
        if (type === 'reaction') {
          notify(t(`live.reactions.${String(data.reaction)}`), 3000);
          return;
        }
        if (type === 'presence') {
          // Presence is a snapshot plus live changes; durable presence events written by older
          // server versions (they carry `at`) are stale replays and are ignored.
          if ('at' in data) return;
          const seat = Number(data.seat);
          const online = Boolean(data.online);
          const before = presenceRef.current[seat];
          presenceRef.current = { ...presenceRef.current, [seat]: online };
          setPresence(presenceRef.current);
          const v = viewRef.current;
          if (
            v?.kind === 'live' &&
            v.result === null &&
            seat !== v.me.seat &&
            before !== undefined &&
            before !== online
          ) {
            const name = v.opponents.find((o) => o.seat === seat)?.displayName;
            notify(
              t(online ? 'live.opponentBack' : 'live.opponentLeft', {
                name: name ?? t('match.opponent'),
              }),
              online ? 3000 : 6000,
            );
          }
          return;
        }
        void load();
      },
      (state) => {
        setConnection(state);
        // Back after a loss: events are replayed from the last id, and the view is refreshed.
        if (state === 'reconnecting') lost = true;
        else if (lost) {
          lost = false;
          void load();
        }
      },
    );
    return () => {
      stop();
      driverRef.current?.dispose();
    };
  }, [params.id, load, t]);

  useEffect(() => {
    if (!view?.parentMatchId) return;
    api<{ matches: { id: string; parent_match_id: string | null }[] }>('GET', '/api/me/matches')
      .then((r) =>
        setSiblings(
          r.matches.filter((m) => m.parent_match_id === view.parentMatchId).map((m) => m.id),
        ),
      )
      .catch(() => setSiblings([]));
  }, [view?.parentMatchId]);

  const start = useCallback(async () => {
    try {
      const v = await api<MatchView>('POST', `/api/matches/${params.id}/start`, {});
      setView(v);
      if (v.me.state && v.rack) {
        driverRef.current = new OnlineDriver(v.id, v.me.state);
        setDriver(driverRef.current);
      }
    } catch {
      setError(t('match.loadError'));
    } finally {
      setCounting(false);
    }
  }, [params.id, t]);

  const onFinished = useCallback(
    (_s: RoundState) => {
      driverRef.current?.dispose();
      driverRef.current = null;
      setDriver(null);
      void load();
    },
    [load],
  );

  if (error)
    return (
      <main className="page">
        <p className="message error">{error}</p>
        <Link href="/">{t('nav.home')}</Link>
      </main>
    );
  if (!view) return <Spinner label={t('common.loading')} />;

  const opp = view.opponents[0];

  const reconnecting =
    connection === 'reconnecting' && view.kind === 'live' && view.result === null ? (
      <div className="banner warn" role="status">
        {t('live.reconnecting')}
      </div>
    ) : null;

  if (driver && view.me.status === 'playing') {
    return (
      <>
        {toast && (
          <div className="toast" role="status">
            {toast}
          </div>
        )}
        {reconnecting}
        <GameScreen
          driver={driver}
          onFinished={onFinished}
          opponent={
            opp
              ? {
                  label: opp.displayName ?? t('match.opponent'),
                  isBot: opp.isBot,
                  progress: () => ({ words: opp.words, finished: opp.status === 'finished' }),
                  online: view.kind === 'live' ? (presence[opp.seat] ?? null) : null,
                }
              : null
          }
        />
      </>
    );
  }

  if (view.kind === 'live' && view.live && view.me.status !== 'finished') {
    return (
      <>
        {toast && (
          <div className="toast" role="status">
            {toast}
          </div>
        )}
        {reconnecting}
        <LiveLobby view={view} presence={presence} onChange={load} />
      </>
    );
  }

  const legTitle =
    view.leg !== null && view.legOrder !== null ? (
      <p className="muted">{t('match.leg', { n: view.legOrder + 1 })}</p>
    ) : null;

  if (view.me.status === 'joined' || view.me.status === 'locked') {
    return (
      <main className="page">
        <h1>{t('match.readyTitle')}</h1>
        {legTitle}
        <section className="card">
          <p>
            <strong>{t(`modes.${view.mode}.name`)}</strong> — {t(`modes.${view.mode}.desc`)}
          </p>
          <p>
            {(view.me.languages ?? []).map((l) => (
              <LangName key={l} lang={l} />
            ))}
          </p>
          <p className="muted small">{t('match.readyBody')}</p>
          <p className="muted small">{view.ranked ? t('match.ranked') : t('match.unranked')}</p>
        </section>
        {counting ? (
          <Countdown onDone={() => void start()} />
        ) : (
          <button className="btn primary big" onClick={() => setCounting(true)}>
            {t('match.start')}
          </button>
        )}
      </main>
    );
  }

  const myState = view.me.state;
  const finished = view.result !== null;
  const nextLeg = siblings.find((s) => s !== view.id);
  return (
    <main className="page">
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
      {legTitle}
      {!finished && (
        <section className="card">
          <p>{t('match.waitingOpponent')}</p>
          {opp && (
            <p className="muted">
              {t('match.opponentProgress', {
                name: opp.displayName ?? t('match.opponent'),
                count: opp.words,
              })}
            </p>
          )}
          {view.kind === 'live' && opp && presence[opp.seat] === false && (
            <p className="muted opponent-away">
              {t('live.opponentLeft', { name: opp.displayName ?? t('match.opponent') })}
            </p>
          )}
          {view.me.score && (
            <p>
              <strong>{formatScore(view.me.score.totalCents, i18n.language)}</strong>{' '}
              {t('results.points')}
            </p>
          )}
        </section>
      )}
      {myState && (
        <ResultsView
          player={myState}
          opponent={
            finished && opp?.state
              ? {
                  label: opp.displayName ?? t('match.opponent'),
                  isBot: opp.isBot,
                  state: opp.state,
                }
              : null
          }
          actions={
            <>
              {view.kind === 'live' && finished && (
                <button
                  className="btn primary big"
                  onClick={async () => {
                    const r = await api<{ matchId: string }>(
                      'POST',
                      `/api/matches/${view.id}/rematch`,
                      {},
                    );
                    navigate(`/match/${r.matchId}`);
                  }}
                >
                  {view.live?.rematchId ? t('live.joinRematch') : t('live.rematch')}
                </button>
              )}
              {view.kind === 'tournament' && view.competition && (
                <Link className="btn primary big" href={`/tournaments/${view.competition.id}`}>
                  {t('tournaments.backToTournament')}
                </Link>
              )}
              {view.kind === 'daily' && (
                <Link className="btn primary big" href="/daily">
                  {t('daily.back')}
                </Link>
              )}
              {nextLeg && view.parentMatchId && (
                <Link className="btn primary big" href={`/match/${nextLeg}`}>
                  {t('match.nextLeg')}
                </Link>
              )}
              <Link className="btn ghost" href="/challenge/new">
                {t('online.challengeFriend')}
              </Link>
              <Link className="btn ghost" href="/">
                {t('nav.home')}
              </Link>
            </>
          }
        />
      )}
      {view.kind === 'live' && <Reactions matchId={view.id} />}
      {opp?.userId && finished && <OpponentActions userId={opp.userId} matchId={view.id} />}
      {finished && opp && !opp.state && <p className="muted">{t('match.opponentNotPlayed')}</p>}
      {view.drawAudit && (
        <p className="muted small">
          {t('results.drawAudit', {
            seed: view.drawAudit.seed,
            gen: view.drawAudit.generatorVersion,
          })}{' '}
          · {t(view.ranked ? 'match.ranked' : 'match.unranked')}
        </p>
      )}
      <ReportWord matchLangs={view.me.languages ?? []} />
      {user?.kind === 'guest' && (
        <aside className="card">
          <p>{t('online.guestBanner')}</p>
          <Link className="btn ghost" href="/account">
            {t('online.saveAccount')}
          </Link>
        </aside>
      )}
    </main>
  );
}

function LiveLobby({
  view,
  presence,
  onChange,
}: {
  view: MatchView;
  presence: Record<number, boolean>;
  onChange: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const settings = useSettings();
  const def = MODES[view.mode];
  const [langs, setLangs] = useState<Lang[]>(settings.lastLanguages.slice(0, def.maxLanguages));
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const live = view.live!;
  const opp = view.opponents[0];
  const invite = recallInvite(view.id);
  const myLocked = view.me.status !== 'joined';
  const reveal = useCallback(() => void onChange(), [onChange]);

  const lock = async () => {
    setError(null);
    try {
      await api(
        'POST',
        `/api/matches/${view.id}/lock`,
        def.languagePolicy === 'individual' ? { languages: langs } : {},
      );
      await onChange();
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === 'no_draw_for_both_configurations'
          ? t('live.noDrawForBoth')
          : t('errors.generic'),
      );
    }
  };

  return (
    <main className="page">
      <h1>{t('online.liveDuel')}</h1>
      <section className="card">
        <p>
          <strong>{t(`modes.${view.mode}.name`)}</strong>
          {view.sharedLanguages && (
            <> · {view.sharedLanguages.map((l) => l.toUpperCase()).join(' + ')}</>
          )}
        </p>
        {opp ? (
          <p>
            {t('match.opponent')} : <strong>{opp.displayName}</strong>{' '}
            <span className="muted small">
              {presence[opp.seat] === false
                ? t('live.offline')
                : presence[opp.seat]
                  ? t('live.online')
                  : ''}
              {live.opponentLocked ? ` · ${t('live.opponentReady')}` : ''}
            </span>
          </p>
        ) : (
          <p className="muted">{t('live.waitingOpponent')}</p>
        )}
      </section>

      {live.phase === 'waiting_opponent' && invite && (
        <section className="card">
          <p>{t('live.shareRoom')}</p>
          <p className="invite-url">
            <code>{invite}</code>
          </p>
          <button
            className="btn ghost"
            onClick={async () => {
              try {
                if (navigator.share) await navigator.share({ url: invite });
                else {
                  await navigator.clipboard.writeText(invite);
                  setCopied(true);
                }
              } catch {
                /* cancelled */
              }
            }}
          >
            {copied ? t('common.copied') : t('challenge.share')}
          </button>
        </section>
      )}

      {(live.phase === 'choosing' || live.phase === 'waiting_opponent') && !myLocked && (
        <section>
          {def.languagePolicy === 'individual' ? (
            <>
              <LanguagePicker
                value={langs}
                onChange={setLangs}
                max={def.maxLanguages}
                label={t('live.languagesSecret')}
              />
              <ContractPreview mode={view.mode} langs={langs} />
            </>
          ) : (
            <p className="muted">{t('live.sharedReady')}</p>
          )}
          {error && <p className="message error">{error}</p>}
          <button
            className="btn primary big"
            disabled={
              def.languagePolicy === 'individual' && validateLanguageSelection(def, langs) !== null
            }
            onClick={() => void lock()}
          >
            {t('live.lock')}
          </button>
        </section>
      )}
      {live.phase === 'choosing' && myLocked && <Spinner label={t('live.waitingLock')} />}
      {live.phase === 'countdown' && live.startsAt && (
        <ServerCountdown startsAt={live.startsAt} onDone={reveal} />
      )}
      {live.phase === 'playing' && <Spinner label={t('common.loading')} />}
      <p className="muted small">{t('live.disconnectRule')}</p>
    </main>
  );
}

function Reactions({ matchId }: { matchId: string }) {
  const { t } = useTranslation();
  const [sent, setSent] = useState<string | null>(null);
  return (
    <section className="reactions row wrap" aria-label={t('live.reactionsTitle')}>
      {REACTIONS.map((r) => (
        <button
          key={r}
          className={`chip${sent === r ? ' selected' : ''}`}
          onClick={async () => {
            await api('POST', `/api/matches/${matchId}/react`, { reaction: r }).catch(() => {});
            setSent(r);
          }}
        >
          {t(`live.reactions.${r}`)}
        </button>
      ))}
    </section>
  );
}

function ReportWord({ matchLangs }: { matchLangs: string[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState('');
  const [lang, setLang] = useState(matchLangs[0] ?? 'fr');
  const [sent, setSent] = useState(false);
  if (sent) return <p className="message ok">{t('match.reportSent')}</p>;
  if (!open)
    return (
      <button className="btn ghost small" onClick={() => setOpen(true)}>
        {t('match.reportWord')}
      </button>
    );
  return (
    <form
      className="card"
      onSubmit={async (e) => {
        e.preventDefault();
        await api('POST', '/api/word-reports', { lang, word });
        setSent(true);
      }}
    >
      <label className="field">
        <span>{t('match.reportPrompt')}</span>
        <input
          type="text"
          value={word}
          onChange={(e) => setWord(e.target.value)}
          maxLength={40}
          required
        />
      </label>
      <select value={lang} onChange={(e) => setLang(e.target.value)}>
        {matchLangs.map((l) => (
          <option key={l} value={l}>
            {l.toUpperCase()}
          </option>
        ))}
      </select>{' '}
      <button className="btn primary small" type="submit">
        {t('common.confirm')}
      </button>
    </form>
  );
}
